import React, { useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { LinkListArrow } from 'anf-core-react';
import { useDatabridge } from '@xp-utilities/web';
import { TmntText, Loading, TmntHtml } from '../Common';
import RewardsLink from './RewardsLink';
import { REWARDS_LINKS_LOGGEDIN_QUERY } from './operations';
import { AccountNavMenuLinksPropTypes, AccountNavMenuLinksDefaultProps } from './props';

function AccountNavMenuLinks({
  isMobileView,
  links,
  onMyAbercrombieClick,
  onOpenAuthModal,
}) {
  const { loading, error, data } = useQuery(REWARDS_LINKS_LOGGEDIN_QUERY, {
    ssr: false,
    context: { batch: true },
    fetchPolicy: 'network-only',
  });

  const isLoggedIn = data?.userState?.isLoggedIn;

  const handleClick = useCallback(
    (url, redirectRoute) => {
      if (url?.includes('/RewardsCenterDisplayView')) {
        return () => onMyAbercrombieClick(url);
      }
      if (url === 'authentication-modal:open') {
        return () => onOpenAuthModal({ isSignInTab: true, redirect: redirectRoute });
      }
      if (url?.includes('/UserAccountView') || url?.includes('/MySavesView') || url?.includes('/myAbercrombie')) {
        return () => window.location.assign(url);
      }
      return null;
    },
    [onMyAbercrombieClick, onOpenAuthModal],
  );

  const transformLinks = useCallback(() => {
    if (!Array.isArray(links)) return { accountLinks: [], rewardsCenterLinks: [] };

    const rewardsCenterLinks = [];

    const accountLinks = links
      .map((link) => {
        const {
          text, optionValue: url, description, redirectRoute,
        } = link;

        const key = text?.key;

        const tmnt = (
          <TmntText
            testId={`account-nav-link-${text?.key}`}
            tmnt={text}
          />
        );

        const renderDescription = (descriptionTmnt) => {
          if (!descriptionTmnt?.value) return null;
          if (descriptionTmnt.key === 'LOYALTY_NEXT_REWARD_CASH') return <TmntHtml tmnt={description} />;

          return <TmntText tmnt={descriptionTmnt} />;
        };

        const descriptionTmnt = renderDescription(description);

        const linkItem = {
          children: (
            <>
              { tmnt }
              { descriptionTmnt }
            </>
          ),
          key,
        };

        const onClickHandler = handleClick(url, redirectRoute);

        if (url?.includes('/RewardsCenterDisplayView')) {
          rewardsCenterLinks.push({ ...linkItem, onClick: onClickHandler });
          return null;
        }

        return onClickHandler
          ? { ...linkItem, onClick: onClickHandler }
          : { ...linkItem, href: url };
      })
      .filter(Boolean);

    return { accountLinks, rewardsCenterLinks };
  }, [links, handleClick]);

  const { accountLinks, rewardsCenterLinks } = useMemo(transformLinks, [transformLinks]);
  const [isCustomerLoyaltyCallsDisabled] = useDatabridge('flag.customer-loyalty-calls-disable-switches.toasterCoupons', false);

  if (error) return null;

  return (
    <Loading isLoading={loading}>
      <div className="account-nav-menu-links">
        {rewardsCenterLinks.length > 0 && (
          <>
            <LinkListArrow links={rewardsCenterLinks} />
            {isLoggedIn && !isCustomerLoyaltyCallsDisabled() && (
              <>
                <RewardsLink isMobileView={isMobileView} />
                <hr className="signin-separator" />
              </>
            )}
          </>
        )}
        <LinkListArrow links={accountLinks} />
        <hr className="signin-separator" />
      </div>
    </Loading>
  );
}

AccountNavMenuLinks.propTypes = AccountNavMenuLinksPropTypes;
AccountNavMenuLinks.defaultProps = AccountNavMenuLinksDefaultProps;

export default AccountNavMenuLinks;
